import { useState } from "react";
import { ClipboardCheck, Paperclip, EyeOff, CheckCircle2, RotateCcw, AlertCircle, User } from "lucide-react";
import Modal from "../components/Modal";
import Badge from "../components/Badge";
import { getSignedUrl } from "../lib/storage";
import { fmtShort } from "../utils";
import { QUALIDADES, corQualidade, rotuloQualidade, VEREDITOS, pendentesDeConferencia, avaliacoes, aplicarAvaliacao } from "../lib/conferencia";

// ═══════════════════════════════════════════════════════════════
// CONFERÊNCIA DO GERENTE
// ═══════════════════════════════════════════════════════════════
//
// O gerente lê o que o comercial registrou e responde duas coisas: confere ou
// manda refazer, e como o trabalho foi feito. É o que fecha o ciclo da trilha
// — registro que ninguém lê não melhora contato nenhum.

const abrirPrint = async (path) => {
  const win = window.open("", "_blank");
  const url = await getSignedUrl(path);
  if (!url) { if (win) win.close(); return; }
  if (win) win.location.href = url; else window.open(url, "_blank");
};

const ICONE = { contato: "Contato", aceite: "Aceite", recusa: "Recusa", justificativa: "Espera" };

export default function ConferenciaGerente({ parceiro, onClose, setPartners, userName = "" }) {
  const [qualidade, setQualidade] = useState({});
  const [comentario, setComentario] = useState({});
  const [erro, setErro] = useState({});

  if (!parceiro) return null;

  const pendentes = pendentesDeConferencia(parceiro);
  const jaFeitas = avaliacoes(parceiro);
  const feitas = Object.values(jaFeitas).sort((a, b) => (b.date || "").localeCompare(a.date || ""));

  const avaliar = (ev, veredito) => {
    const q = qualidade[ev.ref];
    const c = (comentario[ev.ref] || "").trim();
    if (!q) { setErro((p) => ({ ...p, [ev.ref]: "Diga como o trabalho foi feito." })); return; }
    // Mandar refazer sem dizer o que corrigir não ensina nada a quem vai refazer.
    if (veredito === "refazer" && !c) { setErro((p) => ({ ...p, [ev.ref]: "Escreva o que precisa ser corrigido." })); return; }
    setErro((p) => ({ ...p, [ev.ref]: "" }));
    setPartners((prev) => prev.map((p) => (
      p.id === parceiro.id ? aplicarAvaliacao(p, { ref: ev.ref, veredito, qualidade: q, comentario: c, autor: userName }) : p
    )));
  };

  return (
    <Modal open={!!parceiro} onClose={onClose} title={`Conferência — ${parceiro.name}`} w={620}>
      <div style={{ display: "flex", gap: 9, padding: "11px 13px", borderRadius: 9, background: "#f5f3ff", border: "1px solid #ddd6fe", marginBottom: 14 }}>
        <ClipboardCheck size={16} color="#6d28d9" style={{ flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: 11.5, color: "#5b21b6", lineHeight: 1.5 }}>
          Confira o que foi registrado e avalie a qualidade. A avaliação <strong>não muda o estágio</strong> da empresa —
          ela fica na trilha, com seu nome, e é o que mostra ao comercial o que melhorar.
        </div>
      </div>

      {pendentes.length === 0 && (
        <div style={{ padding: "22px 14px", textAlign: "center", color: "#94a3b8", fontSize: 12.5 }}>
          Nada pendente aqui. Tudo o que o comercial registrou nesta empresa já foi conferido.
        </div>
      )}

      {pendentes.map((ev) => (
        <div key={ev.ref} style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "12px 14px", marginBottom: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a" }}>{ev.titulo}</div>
              <div style={{ fontSize: 10.5, color: "#64748b", marginTop: 2, display: "flex", gap: 8, flexWrap: "wrap" }}>
                <span>{fmtShort(ev.data)}</span>
                {ev.contatoEm && <span>às {String(ev.contatoEm).slice(11, 16)}</span>}
                {ev.autor && <span style={{ display: "flex", alignItems: "center", gap: 3 }}><User size={10} /> {ev.autor}</span>}
              </div>
            </div>
            <Badge color="#7c3aed" small>{ICONE[ev.tipo] || ev.tipo}</Badge>
          </div>

          {ev.detalhe && (
            <div style={{ fontSize: 11.5, color: "#475569", marginTop: 7, lineHeight: 1.45, background: "#f8fafc", borderRadius: 7, padding: "7px 10px" }}>
              {ev.detalhe}
            </div>
          )}

          {ev.tipo === "contato" && (
            <div style={{ marginTop: 7 }}>
              {ev.comprovante ? (
                <button onClick={() => abrirPrint(ev.comprovante)} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 6, border: "1px solid #99f6e4", background: "#f0fdfa", color: "#0f766e", fontSize: 10.5, fontWeight: 600, cursor: "pointer" }}>
                  <Paperclip size={11} /> Ver print da conversa
                </button>
              ) : (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10.5, color: "#b45309", fontWeight: 600 }}>
                  <EyeOff size={11} /> registrado sem comprovante
                </span>
              )}
            </div>
          )}

          <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 10 }}>
            {QUALIDADES.map((q) => (
              <button
                key={q.id} title={q.dica}
                onClick={() => { setQualidade((p) => ({ ...p, [ev.ref]: q.id })); setErro((p) => ({ ...p, [ev.ref]: "" })); }}
                style={{
                  padding: "5px 11px", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer",
                  border: `1px solid ${qualidade[ev.ref] === q.id ? q.cor : "#e2e8f0"}`,
                  background: qualidade[ev.ref] === q.id ? `${q.cor}14` : "#fff",
                  color: qualidade[ev.ref] === q.id ? q.cor : "#64748b",
                }}
              >
                {q.label}
              </button>
            ))}
          </div>

          <input
            value={comentario[ev.ref] || ""}
            onChange={(e) => { setComentario((p) => ({ ...p, [ev.ref]: e.target.value })); setErro((p) => ({ ...p, [ev.ref]: "" })); }}
            placeholder="Comentário para o comercial (obrigatório se for refazer)"
            style={{ width: "100%", marginTop: 8, padding: "7px 10px", borderRadius: 7, border: "1px solid #d1d5db", fontSize: 11.5, boxSizing: "border-box", color: "#334155", fontFamily: "inherit" }}
          />

          {erro[ev.ref] && (
            <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10.5, color: "#dc2626", marginTop: 5 }}>
              <AlertCircle size={11} /> {erro[ev.ref]}
            </div>
          )}

          <div style={{ display: "flex", gap: 6, marginTop: 9 }}>
            <button onClick={() => avaliar(ev, "confirmado")} style={{ display: "flex", alignItems: "center", gap: 5, padding: "7px 13px", borderRadius: 7, border: "none", background: "#059669", color: "#fff", fontSize: 11.5, fontWeight: 600, cursor: "pointer" }}>
              <CheckCircle2 size={12} /> Confirmar
            </button>
            <button onClick={() => avaliar(ev, "refazer")} style={{ display: "flex", alignItems: "center", gap: 5, padding: "7px 13px", borderRadius: 7, border: "1px solid #fecaca", background: "#fff", color: "#b91c1c", fontSize: 11.5, fontWeight: 600, cursor: "pointer" }}>
              <RotateCcw size={12} /> Pedir para refazer
            </button>
          </div>
        </div>
      ))}

      {feitas.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 7 }}>
            Já conferido ({feitas.length})
          </div>
          {feitas.slice(0, 8).map((a, i) => (
            <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "6px 0", borderTop: i ? "1px solid #f1f5f9" : "none", fontSize: 11, color: "#475569" }}>
              <Badge color={VEREDITOS[a.veredito]?.cor} small>{VEREDITOS[a.veredito]?.label || a.veredito}</Badge>
              <span style={{ color: corQualidade(a.qualidade), fontWeight: 600 }}>{rotuloQualidade(a.qualidade)}</span>
              <span style={{ flex: 1, minWidth: 0 }}>{a.comentario}</span>
              <span style={{ color: "#94a3b8", whiteSpace: "nowrap" }}>{fmtShort(a.date)}{a.autor ? ` · ${a.autor}` : ""}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12, borderTop: "1px solid #f1f5f9", paddingTop: 12 }}>
        <button onClick={onClose} style={{ padding: "8px 16px", borderRadius: 7, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
          Fechar
        </button>
      </div>
    </Modal>
  );
}
